import { useEffect, useId, useRef, type ReactNode } from "react";
import { X } from "lucide-react";
import { cx } from "../../lib/cx.js";
import { Button } from "./Button.js";

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl" | "full";
  /** Panel lateral (filtros, detalle de auditoría) en vez de ventana centrada. */
  side?: boolean;
  /** Impide cerrar con Esc o clic fuera (p. ej. mientras se guarda). */
  dismissable?: boolean;
}

const SIZES = {
  sm: "sm:max-w-md",
  md: "sm:max-w-lg",
  lg: "sm:max-w-2xl",
  xl: "sm:max-w-4xl",
  full: "sm:max-w-[min(96vw,1400px)]",
} as const;

/**
 * Diálogo modal sobre <dialog> nativo: el navegador ya da foco atrapado, Esc,
 * capa superior e inertes el resto de la página. El contenido se monta solo
 * mientras está abierto, así cada apertura parte con el formulario limpio.
 */
export function Dialog({
  open, onClose, title, description, children, footer, size = "md", side = false, dismissable = true,
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const descId = useId();

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      aria-describedby={description ? descId : undefined}
      onCancel={(e) => {
        e.preventDefault();
        if (dismissable) onClose();
      }}
      onMouseDown={(e) => {
        // Clic en el fondo: el objetivo es el propio <dialog> (el contenido lo cubre entero).
        if (dismissable && e.target === e.currentTarget) onClose();
      }}
      className={cx(
        "max-h-none max-w-none overflow-visible bg-transparent p-0 text-fg",
        side
          ? "my-0 mr-0 ml-auto h-dvh w-full sm:w-[420px]"
          : "mx-auto mt-auto mb-0 h-auto w-full sm:mb-auto",
        !side && SIZES[size],
      )}
    >
      {open && (
        <div
          className={cx(
            "flex flex-col border border-border bg-surface shadow-pop",
            side
              ? "h-full rounded-none sm:rounded-l-xl"
              : "max-h-[92dvh] rounded-t-xl sm:max-h-[88dvh] sm:rounded-xl",
          )}
        >
          <header className="flex items-start gap-3 border-b border-border px-5 py-4">
            <div className="min-w-0 flex-1">
              <h2 id={titleId} className="text-base font-semibold text-fg">
                {title}
              </h2>
              {description && (
                <div id={descId} className="mt-0.5 text-[13px] text-muted">
                  {description}
                </div>
              )}
            </div>
            {dismissable && (
              <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label="Cerrar" className="-mt-1 -mr-2">
                <X className="size-4" aria-hidden="true" />
              </Button>
            )}
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer && (
            <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-border bg-surface-2/60 px-5 py-3 sm:rounded-b-xl">
              {footer}
            </footer>
          )}
        </div>
      )}
    </dialog>
  );
}

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  children: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  pending?: boolean;
  error?: string | null;
}

/** Confirmación explícita para acciones con consecuencias (suspender, deshabilitar…). */
export function ConfirmDialog({
  open, onClose, onConfirm, title, children, confirmLabel, danger = false, pending = false, error,
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      dismissable={!pending}
      footer={
        <>
          <Button onClick={onClose} disabled={pending}>
            Cancelar
          </Button>
          <Button variant={danger ? "danger" : "primary"} onClick={onConfirm} loading={pending}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm text-muted">{children}</div>
      {error && (
        <p role="alert" className="mt-3 text-[13px] font-medium text-danger">
          {error}
        </p>
      )}
    </Dialog>
  );
}
